import type { Channel } from '../../utils/get-channels-from-pypes';
import { isCallRecordingEnabled } from '../../utils/call-recording';
import {
  clampEotThreshold,
  EOT_THRESHOLD_DEFAULT,
  FLUX_SPEECH_MODEL,
  FLUX_USER_SILENCE_TIMEOUT_MIN,
  type MaxSpeakingTimeState,
} from './flux-rules';
import type { VoiceChannelFormValues } from './voice-channel-settings-form';

const isAbsent = (v: unknown): boolean =>
  v == null || v === 'null' || v === 'undefined';

const NEW_CHANNEL_PRE_FLUX_SILENCE: MaxSpeakingTimeState = {
  value: null,
  enabled: false,
};

/**
 * What the form records as "underneath" Flux when it loads, so switching away
 * from Flux can put it back.
 *
 * A new channel starts on Flux only by default: underneath it Max Caller
 * Speaking Time is off, as for any other model, so switching the new channel
 * to Google turns it back off instead of keeping Flux's 10s floor
 * (PSTUDIO-3942). An existing channel records nothing — what it stores is what
 * it has. Keyed on the same input as resolveVoiceChannelInitialValues, so the
 * two cannot disagree about whether the channel is new.
 */
export const resolveInitialPreFluxSilence = (
  selectedChannel: Channel | null | undefined
): MaxSpeakingTimeState | null =>
  selectedChannel ? null : NEW_CHANNEL_PRE_FLUX_SILENCE;

/**
 * The form's starting values: an existing voice channel read back from its
 * stored `twilioVoiceConfig`, or the defaults for a new one.
 */
export const resolveVoiceChannelInitialValues = (
  selectedChannel: Channel | null | undefined
): VoiceChannelFormValues =>
  selectedChannel
    ? {
        botName: selectedChannel?.name || '',
        language: selectedChannel?.twilioVoiceConfig?.language || 'en-US',
        env: selectedChannel?.env || 'sandbox',
        ttsProvider:
          selectedChannel?.twilioVoiceConfig?.tts_provider || 'ElevenLabs',
        speechModel: selectedChannel?.twilioVoiceConfig?.speech_model || 'long',
        transcriptionProvider:
          selectedChannel?.twilioVoiceConfig?.transcription_provider ||
          'Google',
        elevenlabsAudioModel:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_audio_model || '',
        elevenlabsVoiceId:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_voice_id || '',
        elevenlabsSimilarity:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_similarity || 0,
        elevenlabsSpeed:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_speed || 0,
        elevenlabsStability:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_stability || 0,
        elevenlabsVoicePreviewUrl: '',
        elevenlabsVoiceMetadata:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_voice_metadata,
        accountSid: selectedChannel?.twilioVoiceConfig?.account_sid || '',
        authToken: selectedChannel?.twilioVoiceConfig?.auth_token || '',
        phoneNumber: selectedChannel?.twilioVoiceConfig?.phone_number || '',
        webhookUrl:
          selectedChannel?.twilioVoiceConfig?.init_call_webhook_url || '',
        laPlatform:
          selectedChannel?.twilioVoiceConfig?.live_agent_config?.integrator ||
          '',
        laClientId:
          selectedChannel?.twilioVoiceConfig?.live_agent_config?.client_id ||
          '',
        laClientSecret:
          selectedChannel?.twilioVoiceConfig?.live_agent_config
            ?.client_secret || '',
        laGrantType:
          selectedChannel?.twilioVoiceConfig?.live_agent_config?.grant_type ||
          '',
        laInstanceUrl:
          selectedChannel?.twilioVoiceConfig?.live_agent_config?.instance_url ||
          '',
        laOAuthUrl:
          selectedChannel?.twilioVoiceConfig?.live_agent_config?.o_auth_url ||
          '',
        handoffRules: (() => {
          const opts = selectedChannel?.twilioVoiceConfig?.handoff_options;
          if (!opts || Object.keys(opts).length === 0) {
            return [{ skillTag: 'general', destination: '' }];
          }
          const rules = Object.entries(opts).map(([skillTag, val]) => ({
            skillTag,
            destination: val.destination,
            type: (val.type as 'phone_number' | 'sip') ?? 'phone_number',
          }));
          const generalRule = rules.find((r) => r.skillTag === 'general');
          const otherRules = rules.filter((r) => r.skillTag !== 'general');
          return [
            generalRule ?? {
              skillTag: 'general',
              destination: '',
              type: 'phone_number' as const,
            },
            ...otherRules,
          ];
        })(),
        inboundMessageBuffer: (() => {
          const v =
            selectedChannel?.twilioVoiceConfig?.inbound_message_buffer_ms;
          return isAbsent(v) ? 1000 : Number(v);
        })(),
        userSilenceTimeout: (() => {
          const v = selectedChannel?.twilioVoiceConfig?.user_silence_timeout;
          return isAbsent(v) ? null : Number(v);
        })(),
        eotThreshold: (() => {
          const v = selectedChannel?.twilioVoiceConfig?.eot_threshold;
          return isAbsent(v)
            ? EOT_THRESHOLD_DEFAULT
            : clampEotThreshold(Number(v));
        })(),
        promptMaxRetries: (() => {
          const v = selectedChannel?.twilioVoiceConfig?.prompt_max_retries;
          return isAbsent(v) ? undefined : v;
        })(),
        retryPromptPrefix: (() => {
          const v = selectedChannel?.twilioVoiceConfig?.retry_prompt_prefix;
          return isAbsent(v) ? undefined : v;
        })(),
        // Read through the helper, not by truthiness: the Channels API
        // stores an absent value as the STRING 'undefined' (PSTUDIO-4090).
        enableCallRecording: isCallRecordingEnabled(selectedChannel),
      }
    : {
        botName: '',
        language: 'en-US',
        env: 'sandbox',
        ttsProvider: 'ElevenLabs',
        // Deepgram is the recommended STT (PSTUDIO-3942), and Flux is the model
        // the form itself picks for it. This is the NEW-channel default only:
        // a stored channel with no provider keeps reading as Google above.
        speechModel: FLUX_SPEECH_MODEL,
        transcriptionProvider: 'Deepgram',
        elevenlabsAudioModel: '',
        elevenlabsVoiceId: '',
        elevenlabsSimilarity: 0.6,
        elevenlabsSpeed: 0.8,
        elevenlabsStability: 0.8,
        elevenlabsVoicePreviewUrl: '',
        elevenlabsVoiceMetadata: undefined,
        accountSid: '',
        authToken: '',
        phoneNumber: '',
        webhookUrl: '',
        laPlatform: '',
        laClientId: '',
        laClientSecret: '',
        laGrantType: '',
        laInstanceUrl: '',
        laOAuthUrl: '',
        handoffRules: [
          {
            skillTag: 'general',
            destination: '',
            type: 'phone_number' as const,
          },
        ],
        inboundMessageBuffer: 1000,
        // Already at the Flux floor, so loading the Flux default corrects
        // nothing. A load correction is applied with shouldDirty, which would
        // open the Create form with the unsaved-changes guard armed.
        userSilenceTimeout: FLUX_USER_SILENCE_TIMEOUT_MIN,
        eotThreshold: EOT_THRESHOLD_DEFAULT,
        promptMaxRetries: undefined,
        retryPromptPrefix: "I'm sorry, I didn't quite catch that.",
        // Opt-in: a new channel does not record until someone says so.
        enableCallRecording: false,
      };
